<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDateLocale } from 'dashboard/composables/useTimeAgo';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import CatalogAPI from 'dashboard/api/catalog';

const { t } = useI18n();
const { formatDate } = useDateLocale();

const dialogRef = ref(null);
const event = ref(null);
const catalogProduct = ref(null);
const loadingProduct = ref(false);

const PRODUCT_EVENT_TYPES = new Set([
  'product_viewed',
  'product_added_to_cart',
  'product_removed_from_cart',
]);

const open = evt => {
  event.value = evt;
  dialogRef.value?.open();
};

const closeModal = () => dialogRef.value?.close();

defineExpose({ open });

const close = () => {
  event.value = null;
  catalogProduct.value = null;
};

watch(event, async newEvent => {
  catalogProduct.value = null;
  if (!newEvent || !PRODUCT_EVENT_TYPES.has(newEvent.event_type)) return;
  const externalId = newEvent.payload?.product?.id;
  if (!externalId) return;

  loadingProduct.value = true;
  try {
    const { data } = await CatalogAPI.getProductByExternalId(
      externalId,
      newEvent.platform
    );
    catalogProduct.value = data.products?.[0] || null;
  } catch {
    // silently ignore — tracking events may predate catalog imports
  } finally {
    loadingProduct.value = false;
  }
});

const catalogVariant = computed(() => {
  if (!catalogProduct.value) return null;
  const variantId = event.value?.payload?.product?.variant_id;
  if (!variantId) return catalogProduct.value.variants[0] || null;
  return (
    catalogProduct.value.variants.find(
      v => String(v.external_id) === String(variantId)
    ) || null
  );
});

const EVENT_ICONS = {
  page_viewed: 'i-lucide-globe',
  product_viewed: 'i-lucide-package',
  collection_viewed: 'i-lucide-layout-grid',
  search_submitted: 'i-lucide-search',
  cart_viewed: 'i-lucide-shopping-cart',
  product_added_to_cart: 'i-lucide-shopping-cart',
  product_removed_from_cart: 'i-lucide-shopping-cart',
  checkout_started: 'i-lucide-credit-card',
  customer_identified: 'i-lucide-user-check',
  order_completed: 'i-lucide-shopping-bag',
};

const eventIcon = computed(
  () => EVENT_ICONS[event.value?.event_type] || 'i-lucide-activity'
);

const eventLabel = computed(() => {
  if (!event.value) return '';
  const p = event.value.payload || {};
  const name = p.product?.title || p.collection?.title || '';
  const query = p.search_query || '';
  switch (event.value.event_type) {
    case 'product_viewed':
      return t('CONTACTS_LAYOUT.ACTIVITIES.TRACKING.PRODUCT_VIEWED', { name });
    case 'collection_viewed':
      return t('CONTACTS_LAYOUT.ACTIVITIES.TRACKING.COLLECTION_VIEWED', {
        name,
      });
    case 'search_submitted':
      return t('CONTACTS_LAYOUT.ACTIVITIES.TRACKING.SEARCH_SUBMITTED', {
        query,
      });
    case 'cart_viewed':
      return t('CONTACTS_LAYOUT.ACTIVITIES.TRACKING.CART_VIEWED');
    case 'product_added_to_cart':
      return t('CONTACTS_LAYOUT.ACTIVITIES.TRACKING.PRODUCT_ADDED_TO_CART', {
        name,
      });
    case 'product_removed_from_cart':
      return t(
        'CONTACTS_LAYOUT.ACTIVITIES.TRACKING.PRODUCT_REMOVED_FROM_CART',
        { name }
      );
    case 'checkout_started':
      return t('CONTACTS_LAYOUT.ACTIVITIES.TRACKING.CHECKOUT_STARTED');
    case 'order_completed':
      return t('CONTACTS_LAYOUT.ACTIVITIES.TRACKING.ORDER_COMPLETED');
    case 'customer_identified':
      return t('CONTACTS_LAYOUT.ACTIVITIES.TRACKING.CUSTOMER_IDENTIFIED');
    default:
      return t('CONTACTS_LAYOUT.ACTIVITIES.TRACKING.PAGE_VIEWED');
  }
});

const eventUrl = computed(() => event.value?.payload?.url || null);

const formattedDate = computed(() =>
  event.value ? formatDate(event.value.occurred_at, 'd MMM yyyy, HH:mm:ss') : ''
);

const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'utm_id',
];

const utms = computed(() => {
  const url = eventUrl.value;
  if (!url) return [];
  try {
    const parsed = new URL(url);
    return UTM_KEYS.filter(k => parsed.searchParams.has(k)).map(k => ({
      key: k.replace('utm_', ''),
      value: parsed.searchParams.get(k),
    }));
  } catch {
    return [];
  }
});

const cleanUrl = computed(() => {
  const url = eventUrl.value;
  if (!url) return null;
  try {
    const parsed = new URL(url);
    UTM_KEYS.forEach(k => parsed.searchParams.delete(k));
    const result = parsed.toString();
    return result.endsWith('?') ? result.slice(0, -1) : result;
  } catch {
    return url;
  }
});
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('CONTACTS_LAYOUT.TRACKING_EVENT_MODAL.TITLE')"
    :show-confirm-button="false"
    :cancel-button-label="t('CONTACTS_LAYOUT.TRACKING_EVENT_MODAL.CLOSE')"
    @close="close"
  >
    <template #header-actions>
      <Button
        variant="ghost"
        color="slate"
        size="sm"
        icon="i-lucide-x"
        class="self-center"
        @click="closeModal"
      />
    </template>

    <div v-if="event" class="flex flex-col gap-4">
      <div class="flex items-center gap-3">
        <div
          class="flex items-center justify-center rounded-full size-8 bg-n-alpha-2 shrink-0"
        >
          <i :class="eventIcon" class="text-base text-n-slate-11" />
        </div>
        <div class="min-w-0">
          <p class="text-sm font-medium text-n-slate-12">{{ eventLabel }}</p>
          <p class="text-xs text-n-slate-10">{{ formattedDate }}</p>
        </div>
      </div>

      <!-- Catalog product card -->
      <div
        v-if="catalogProduct"
        class="flex items-center gap-3 rounded-lg bg-n-alpha-1 p-3"
      >
        <img
          v-if="catalogVariant?.image_url || catalogProduct.image_url"
          :src="catalogVariant?.image_url || catalogProduct.image_url"
          :alt="catalogProduct.title"
          class="size-12 rounded-lg object-cover shrink-0 bg-n-alpha-2"
        />
        <div class="flex flex-col gap-0.5 min-w-0">
          <p class="text-sm font-medium text-n-slate-12 truncate">
            {{ catalogProduct.title }}
          </p>
          <p v-if="catalogVariant" class="text-xs text-n-slate-11 truncate">
            {{ catalogVariant.title }}
          </p>
          <div class="flex items-center gap-2 text-xs text-n-slate-10">
            <span v-if="catalogVariant?.sku">
              {{
                t('CONTACTS_LAYOUT.TRACKING_EVENT_MODAL.SKU', {
                  sku: catalogVariant.sku,
                })
              }}
            </span>
            <span
              v-if="catalogVariant?.price"
              class="font-medium text-n-slate-11"
            >
              {{ catalogVariant.currency }} {{ catalogVariant.price }}
            </span>
          </div>
        </div>
      </div>

      <div v-if="eventUrl" class="flex flex-col gap-1.5">
        <p class="text-xs font-medium text-n-slate-11 uppercase tracking-wide">
          {{ t('CONTACTS_LAYOUT.TRACKING_EVENT_MODAL.URL') }}
        </p>
        <a
          :href="eventUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="text-sm text-n-blue-10 break-all hover:underline"
        >
          {{ cleanUrl }}
        </a>
      </div>

      <div v-if="utms.length" class="flex flex-col gap-1.5">
        <p class="text-xs font-medium text-n-slate-11 uppercase tracking-wide">
          {{ t('CONTACTS_LAYOUT.TRACKING_EVENT_MODAL.UTM_PARAMS') }}
        </p>
        <div class="flex flex-col gap-1.5 rounded-lg bg-n-alpha-1 px-3 py-2.5">
          <div
            v-for="utm in utms"
            :key="utm.key"
            class="flex items-start gap-3 text-sm"
          >
            <span class="text-n-slate-10 shrink-0 w-20 capitalize">{{
              utm.key
            }}</span>
            <span class="text-n-slate-12 break-all">{{ utm.value }}</span>
          </div>
        </div>
      </div>
    </div>
  </Dialog>
</template>
